/**
 * Self-test suite for the call-ringing Web Push primitives.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallRingingSelfTest.swift`.
 *
 * Three layers, each isolated from system APIs so the suite runs in dev
 * mode without touching Service Worker registration, Notification API, or
 * the network:
 *
 *   A. Push payload parser (pure JSON object → value type).
 *   B. Pure state machine for the ring lifecycle.
 *   C. Pre-trust + rate-limit filter, time-advanced via an injected clock.
 *
 * All time-sensitive cases use an explicit `now` argument so the test can
 * step the clock deterministically — no sleeps, no real timers.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  CallRingingPushError,
  CallRingingPushParser,
} from "./CallRingingPayload";
import type { CallRingingPushPayload, CallKind } from "./CallRingingPayload";
import { CallRingingTransitions } from "./CallRingingState";
import type { CallRingingSession } from "./CallRingingState";
import { CallRingingFilter } from "./CallRingingFilter";

SelfTestRunner.register({
  name: "Call ringing (Web Push + Notification)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. Parser
      testParserRoundTripsValidPayload(),
      testParserMissingKaetherKey(),
      testParserMissingField(),
      testParserUnsupportedSchema(),
      testParserEmptyCallId(),
      testParserEmptyCallerId(),
      testParserFutureTimestamp(),

      // B. State machine
      testIncomingPushArrivedSameCallIdIsIdempotent(),
      testIncomingDescriptorFetchedAdvancesToRinging(),
      testRingingUserAnsweredAdvancesToAnswered(),
      testRingingUserDeclinedAdvancesToDeclined(),
      testRingingRemoteHungUpAdvancesToMissed(),
      testAnsweredRemoteHungUpAdvancesToEnded(),
      testInvalidTransitionReturnsNull(),
      testTickEnforcesDeadlines(),

      // C. Filter
      testUnknownPeerBlockedWithUnknownCallerUI(),
      testKnownPeerFirstRingAllowed(),
      testRateLimitAfterFiveRings(),
      testWindowExpiresThenAllowsAgain(),
      testSetKnownPeersPropagates(),
      testMultiplePeersTrackedIndependently(),
      testResetClearsWindowState(),
    ]),
});

// ---------------------------------------------------------------------------
// A. Parser
// ---------------------------------------------------------------------------

function testParserRoundTripsValidPayload(): TestCase {
  const body = {
    kaether: {
      schema: 1,
      callId: "01HXXXXXXXXXXXXXXXXXXXX",
      callerUserId: "alice",
      pushedAt: 1_700_000_000,
      kind: "audio",
    },
  };
  try {
    const payload = CallRingingPushParser.parse(body, 1_700_000_100);
    const ok =
      payload.schema === 1 &&
      payload.callId === "01HXXXXXXXXXXXXXXXXXXXX" &&
      payload.callerUserId === "alice" &&
      payload.pushedAt === 1_700_000_000 &&
      payload.kind === "audio";
    return ok
      ? pass("parser round-trips a valid push payload")
      : fail("parser round-trips a valid push payload", `unexpected payload: ${JSON.stringify(payload)}`);
  } catch (e) {
    return fail("parser round-trips a valid push payload", `threw: ${e}`);
  }
}

function testParserMissingKaetherKey(): TestCase {
  const body = { aps: { alert: "hi" } };
  try {
    CallRingingPushParser.parse(body, 1_700_000_000);
    return fail("missing kaether sub-object is rejected", "did not throw");
  } catch (e) {
    if (e instanceof CallRingingPushError && e.detail.kind === "malformedPushPayload") {
      return pass("missing kaether sub-object is rejected");
    }
    return fail("missing kaether sub-object is rejected", `wrong error: ${e}`);
  }
}

function testParserMissingField(): TestCase {
  // Missing "kind" — every other field present.
  const body = {
    kaether: {
      schema: 1,
      callId: "01H",
      callerUserId: "alice",
      pushedAt: 1_700_000_000,
      // kind omitted
    },
  };
  try {
    CallRingingPushParser.parse(body, 1_700_000_000);
    return fail("missing required field is rejected", "did not throw");
  } catch (e) {
    if (e instanceof CallRingingPushError && e.detail.kind === "malformedPushPayload") {
      return pass("missing required field is rejected");
    }
    return fail("missing required field is rejected", `wrong error: ${e}`);
  }
}

function testParserUnsupportedSchema(): TestCase {
  const body = {
    kaether: {
      schema: 99,
      callId: "01H",
      callerUserId: "alice",
      pushedAt: 1_700_000_000,
      kind: "audio",
    },
  };
  try {
    CallRingingPushParser.parse(body, 1_700_000_000);
    return fail("unsupported schema is rejected", "did not throw");
  } catch (e) {
    if (
      e instanceof CallRingingPushError &&
      e.detail.kind === "unsupportedSchema" &&
      e.detail.schema === 99
    ) {
      return pass("unsupported schema is rejected");
    }
    return fail("unsupported schema is rejected", `wrong error: ${e}`);
  }
}

function testParserEmptyCallId(): TestCase {
  const body = {
    kaether: {
      schema: 1,
      callId: "",
      callerUserId: "alice",
      pushedAt: 1_700_000_000,
      kind: "audio",
    },
  };
  try {
    CallRingingPushParser.parse(body, 1_700_000_000);
    return fail("empty callId is rejected", "did not throw");
  } catch (e) {
    if (e instanceof CallRingingPushError && e.detail.kind === "emptyCallId") {
      return pass("empty callId is rejected");
    }
    return fail("empty callId is rejected", `wrong error: ${e}`);
  }
}

function testParserEmptyCallerId(): TestCase {
  const body = {
    kaether: {
      schema: 1,
      callId: "01H",
      callerUserId: "",
      pushedAt: 1_700_000_000,
      kind: "audio",
    },
  };
  try {
    CallRingingPushParser.parse(body, 1_700_000_000);
    return fail("empty callerUserId is rejected", "did not throw");
  } catch (e) {
    if (e instanceof CallRingingPushError && e.detail.kind === "emptyCallerId") {
      return pass("empty callerUserId is rejected");
    }
    return fail("empty callerUserId is rejected", `wrong error: ${e}`);
  }
}

function testParserFutureTimestamp(): TestCase {
  const now = 1_700_000_000;
  const body = {
    kaether: {
      schema: 1,
      callId: "01H",
      callerUserId: "alice",
      pushedAt: now + 120, // 120s ahead — beyond 60s skew
      kind: "audio",
    },
  };
  try {
    CallRingingPushParser.parse(body, now);
    return fail("future pushedAt is rejected", "did not throw");
  } catch (e) {
    if (e instanceof CallRingingPushError && e.detail.kind === "futureTimestamp") {
      return pass("future pushedAt is rejected");
    }
    return fail("future pushedAt is rejected", `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B. State machine
// ---------------------------------------------------------------------------

function freshIncoming(pushedAt: number): CallRingingSession {
  return {
    callId: "01HXXXXXXXXXXXXXXXXXXXX",
    callerUserId: "alice",
    kind: "audio" as CallKind,
    pushedAt,
    state: "incoming",
    lastEventAt: pushedAt,
  };
}

function testIncomingPushArrivedSameCallIdIsIdempotent(): TestCase {
  const s = freshIncoming(1_000);
  const next = CallRingingTransitions.transition(
    s,
    { type: "pushArrived", callId: s.callId, at: 1_005 },
    1_005,
  );
  const ok = next !== null && next.state === "incoming" && next.lastEventAt === 1_005;
  return ok
    ? pass(".incoming + pushArrived(same callId) stays .incoming (idempotent re-ring)")
    : fail(
        ".incoming + pushArrived(same callId) stays .incoming (idempotent re-ring)",
        `got state=${next?.state} lastEventAt=${next?.lastEventAt}`,
      );
}

function testIncomingDescriptorFetchedAdvancesToRinging(): TestCase {
  const s = freshIncoming(1_000);
  const next = CallRingingTransitions.transition(
    s,
    { type: "descriptorFetched", callId: s.callId },
    1_010,
  );
  const ok = next?.state === "ringing";
  return ok
    ? pass(".incoming + descriptorFetched → .ringing")
    : fail(".incoming + descriptorFetched → .ringing", `got state=${next?.state}`);
}

function testRingingUserAnsweredAdvancesToAnswered(): TestCase {
  const s: CallRingingSession = {
    ...freshIncoming(1_000),
    state: "ringing",
    lastEventAt: 1_010,
  };
  const next = CallRingingTransitions.transition(s, { type: "userAnswered" }, 1_020);
  const ok = next?.state === "answered";
  return ok
    ? pass(".ringing + userAnswered → .answered")
    : fail(".ringing + userAnswered → .answered", `got state=${next?.state}`);
}

function testRingingUserDeclinedAdvancesToDeclined(): TestCase {
  const s: CallRingingSession = {
    ...freshIncoming(1_000),
    state: "ringing",
    lastEventAt: 1_010,
  };
  const next = CallRingingTransitions.transition(s, { type: "userDeclined" }, 1_020);
  const ok = next?.state === "declined";
  return ok
    ? pass(".ringing + userDeclined → .declined")
    : fail(".ringing + userDeclined → .declined", `got state=${next?.state}`);
}

function testRingingRemoteHungUpAdvancesToMissed(): TestCase {
  const s: CallRingingSession = {
    ...freshIncoming(1_000),
    state: "ringing",
    lastEventAt: 1_010,
  };
  const next = CallRingingTransitions.transition(s, { type: "remoteHungUp" }, 1_020);
  const ok = next?.state === "missed";
  return ok
    ? pass(".ringing + remoteHungUp → .missed (caller bailed before pickup)")
    : fail(".ringing + remoteHungUp → .missed (caller bailed before pickup)", `got state=${next?.state}`);
}

function testAnsweredRemoteHungUpAdvancesToEnded(): TestCase {
  const s: CallRingingSession = {
    ...freshIncoming(1_000),
    state: "answered",
    lastEventAt: 1_020,
  };
  const next = CallRingingTransitions.transition(s, { type: "remoteHungUp" }, 1_050);
  const ok = next?.state === "ended";
  return ok
    ? pass(".answered + remoteHungUp → .ended")
    : fail(".answered + remoteHungUp → .ended", `got state=${next?.state}`);
}

function testInvalidTransitionReturnsNull(): TestCase {
  // userAnswered from .incoming makes no sense — no notification was ever shown.
  const s = freshIncoming(1_000);
  const next = CallRingingTransitions.transition(s, { type: "userAnswered" }, 1_010);
  const ok = next === null;
  return ok
    ? pass("invalid transition (userAnswered from .incoming) returns null")
    : fail("invalid transition (userAnswered from .incoming) returns null", `got state=${next?.state}`);
}

function testTickEnforcesDeadlines(): TestCase {
  // 1. .incoming → .timedOut after osDeadline.
  const incoming = freshIncoming(1_000);
  const tickedOs = CallRingingTransitions.tick(
    incoming,
    1_000 + CallRingingTransitions.osDeadlineSeconds + 1,
  );
  if (tickedOs?.state !== "timedOut") {
    return fail(
      "tick advances .incoming → .timedOut and .ringing → .missed",
      `OS deadline not enforced: state=${tickedOs?.state}`,
    );
  }
  // No tick before deadline.
  const preDeadline = CallRingingTransitions.tick(
    incoming,
    1_000 + CallRingingTransitions.osDeadlineSeconds - 1,
  );
  if (preDeadline !== null) {
    return fail(
      "tick advances .incoming → .timedOut and .ringing → .missed",
      `OS deadline triggered prematurely: state=${preDeadline?.state}`,
    );
  }
  // 2. .ringing → .missed after answerDeadline.
  const ringing: CallRingingSession = {
    ...freshIncoming(1_000),
    state: "ringing",
    lastEventAt: 1_010,
  };
  const tickedAnswer = CallRingingTransitions.tick(
    ringing,
    1_010 + CallRingingTransitions.answerDeadlineSeconds + 1,
  );
  if (tickedAnswer?.state !== "missed") {
    return fail(
      "tick advances .incoming → .timedOut and .ringing → .missed",
      `answer deadline not enforced: state=${tickedAnswer?.state}`,
    );
  }
  return pass("tick advances .incoming → .timedOut and .ringing → .missed");
}

// ---------------------------------------------------------------------------
// C. Filter
// ---------------------------------------------------------------------------

function samplePayload(caller: string, pushedAt: number): CallRingingPushPayload {
  return {
    schema: 1,
    callId: `01H-${caller}-${pushedAt}`,
    callerUserId: caller,
    pushedAt,
    kind: "audio" as CallKind,
  };
}

function testUnknownPeerBlockedWithUnknownCallerUI(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  const decision = filter.evaluate(samplePayload("stranger", 1_000), 1_000);
  const ok =
    !decision.allowed &&
    decision.synthesiseUnknownCallerUI &&
    decision.reason === "unknown peer";
  return ok
    ? pass("unknown peer → blocked, synthesiseUnknownCallerUI=true")
    : fail("unknown peer → blocked, synthesiseUnknownCallerUI=true", JSON.stringify(decision));
}

function testKnownPeerFirstRingAllowed(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  const decision = filter.evaluate(samplePayload("alice", 1_000), 1_000);
  return decision.allowed
    ? pass("known peer first ring → allowed")
    : fail("known peer first ring → allowed", JSON.stringify(decision));
}

function testRateLimitAfterFiveRings(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  // Five rings inside the window — each at t+0, t+1, ..., t+4.
  for (let i = 0; i < 5; i++) {
    const now = 1_000 + i;
    const d = filter.evaluate(samplePayload("alice", now), now);
    if (!d.allowed) {
      return fail(
        "6th ring within 30s → rate limited, synthesiseUnknownCallerUI=false",
        `ring #${i + 1} was unexpectedly blocked: ${JSON.stringify(d)}`,
      );
    }
  }
  // Sixth ring inside the same window must be blocked.
  const sixth = filter.evaluate(samplePayload("alice", 1_005), 1_005);
  const ok =
    !sixth.allowed &&
    !sixth.synthesiseUnknownCallerUI &&
    sixth.reason === "rate limited";
  return ok
    ? pass("6th ring within 30s → rate limited, synthesiseUnknownCallerUI=false")
    : fail(
        "6th ring within 30s → rate limited, synthesiseUnknownCallerUI=false",
        JSON.stringify(sixth),
      );
}

function testWindowExpiresThenAllowsAgain(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  for (let i = 0; i < 5; i++) {
    filter.evaluate(samplePayload("alice", 1_000 + i), 1_000 + i);
  }
  // Inside window — should be blocked.
  const blocked = filter.evaluate(samplePayload("alice", 1_010), 1_010);
  if (blocked.allowed) {
    return fail("after window expires, rings allowed again", `rate limiter never engaged: ${JSON.stringify(blocked)}`);
  }
  // Step 60s past the last ring — window fully expired.
  const later = 1_005 + 60;
  const allowed = filter.evaluate(samplePayload("alice", later), later);
  return allowed.allowed
    ? pass("after window expires, rings allowed again")
    : fail("after window expires, rings allowed again", JSON.stringify(allowed));
}

function testSetKnownPeersPropagates(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  // bob unknown initially.
  const first = filter.evaluate(samplePayload("bob", 1_000), 1_000);
  if (first.allowed || !first.synthesiseUnknownCallerUI) {
    return fail("setKnownPeers update propagates", `bob unexpectedly trusted: ${JSON.stringify(first)}`);
  }
  // Add bob.
  filter.setKnownPeers(new Set(["alice", "bob"]));
  const second = filter.evaluate(samplePayload("bob", 1_001), 1_001);
  return second.allowed
    ? pass("setKnownPeers update propagates")
    : fail("setKnownPeers update propagates", JSON.stringify(second));
}

function testMultiplePeersTrackedIndependently(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice", "bob"]));
  // Alice rings 5 times — exhausts her budget.
  for (let i = 0; i < 5; i++) {
    filter.evaluate(samplePayload("alice", 1_000 + i), 1_000 + i);
  }
  const aliceSixth = filter.evaluate(samplePayload("alice", 1_010), 1_010);
  if (aliceSixth.allowed) {
    return fail("multiple peers tracked independently", `alice rate limiter never engaged: ${JSON.stringify(aliceSixth)}`);
  }
  // Bob's first ring must still pass — his window is separate.
  const bobFirst = filter.evaluate(samplePayload("bob", 1_010), 1_010);
  return bobFirst.allowed
    ? pass("multiple peers tracked independently")
    : fail("multiple peers tracked independently", `bob blocked despite separate window: ${JSON.stringify(bobFirst)}`);
}

function testResetClearsWindowState(): TestCase {
  const filter = new CallRingingFilter({ windowSeconds: 30, maxRingsPerWindow: 5 });
  filter.setKnownPeers(new Set(["alice"]));
  for (let i = 0; i < 5; i++) {
    filter.evaluate(samplePayload("alice", 1_000 + i), 1_000 + i);
  }
  const blocked = filter.evaluate(samplePayload("alice", 1_010), 1_010);
  if (blocked.allowed) {
    return fail("reset() clears window state", `rate limiter never engaged: ${JSON.stringify(blocked)}`);
  }
  filter.reset();
  const afterReset = filter.evaluate(samplePayload("alice", 1_011), 1_011);
  return afterReset.allowed
    ? pass("reset() clears window state")
    : fail("reset() clears window state", JSON.stringify(afterReset));
}
